// A coalescing mutex. Wraps a task so it runs AT MOST ONCE AT A TIME; requests arriving
// while it runs collapse into EXACTLY ONE re-run afterwards, no matter how many arrive.
//
//   const request = coalesce(async () => { await whenReady(); await recompute() })
//   request(); request(); request()   // -> task runs, then runs once more
//
// Why a primitive rather than flags on the caller: the "am I already running / did
// something change while I ran" pair is only correct when the statements appear in the
// right order, and the fragile ordering (an `await` BEFORE the guard, so the guard reflects
// post-await state) is invisible at a glance and breaks silently under refactoring. Here
// the whole cycle lives in one loop with nothing to mis-order, and the ordering hazard
// disappears entirely when the wait is simply the task's first step.
//
// Guarantees:
//   - runs never overlap
//   - N requests during a run produce exactly 1 re-run (they coalesce)
//   - a THROWING task doesn't wedge it: `running` clears in a finally, so the next
//     request starts a fresh chain (the rejection still propagates to whoever awaits)
//   - request() returns an awaitable that settles when the run chain it joined does
export function coalesce(task) {
  let running = null
  let queued = false
  return function request() {
    if (running) {
      queued = true
      return running
    }
    running = (async () => {
      try {
        do {
          queued = false
          await task()
        } while (queued)
      } finally {
        running = null
      }
    })()
    return running
  }
}
